import { Link } from 'react-router-dom';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import SectionHeading from '@/pages/home/components/SectionHeading';
import MediaCard from '@/components/feature/MediaCard';
import NewsRow from '@/components/feature/NewsRow';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getAllMedia, getNews, sortNewsByDate } from '@/lib/catalog';

const CATEGORY_CARDS = [
  {
    type: 'movie',
    title: '电影',
    desc: '最新院线大片与热门佳片，按类型、地区、年份一键筛选。',
    icon: 'ri-movie-2-line',
    image:
      'https://readdy.ai/api/search-image?query=cinema%20concept%2C%20row%20of%20red%20velvet%20seats%20facing%20a%20bright%20glowing%20screen%2C%20dramatic%20spotlight%2C%20deep%20navy%20and%20amber%20tones%2C%20no%20text&width=600&height=400&seq=home-cat-movie&orientation=landscape'
  },
  {
    type: 'tv',
    title: '电视剧',
    desc: '追剧不迷路，古装、悬疑、都市、甜宠，各类好剧一站尽览。',
    icon: 'ri-tv-2-line',
    image:
      'https://readdy.ai/api/search-image?query=cozy%20living%20room%20with%20large%20TV%20showing%20a%20series%2C%20warm%20evening%20lighting%2C%20comfortable%20sofa%2C%20inviting%20teal%20and%20amber%20tones%2C%20no%20text&width=600&height=400&seq=home-cat-tv&orientation=landscape'
  },
  {
    type: 'news',
    title: '影视资讯',
    desc: '行业动态、市场前瞻与深度解读，掌握影视圈最新风向。',
    icon: 'ri-newspaper-line',
    image:
      'https://readdy.ai/api/search-image?query=modern%20editorial%20desk%20with%20newspapers%20and%20a%20tablet%2C%20warm%20desk%20lamp%20light%2C%20clean%20professional%20tones%2C%20no%20text&width=600&height=400&seq=home-cat-news&orientation=landscape'
  }
];

export default function Home() {
  useDocumentTitle(
    '风驰影院-最新电影电视剧播放与影视资讯',
    '风驰影院为您提供丰富的最新电影、电视剧资源与权威影视资讯，涵盖多种类型，让您轻松查找并了解热门影视作品动态。'
  );

  const allMedia = getAllMedia();
  const latestMovies = allMedia
    .filter((item) => item.kind === 'movie')
    .sort((a, b) => b.year - a.year || b.popularity - a.popularity)
    .slice(0, 6);
  const latestTvs = allMedia
    .filter((item) => item.kind === 'tv')
    .sort((a, b) => b.year - a.year || b.popularity - a.popularity)
    .slice(0, 6);
  const latestNews = sortNewsByDate(getNews()).slice(0, 7);

  return (
    <div className="w-full">
      <HeroCarousel />

      <div className="mx-auto w-full max-w-[1240px] px-4 md:px-6">
        {/* 分类快捷入口 */}
        <section className="-mt-10 relative z-20 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {CATEGORY_CARDS.map((card) => (
            <Link
              key={card.type}
              to={`/category/${card.type}`}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:border-primary-300"
            >
              <div className="relative h-28 w-full overflow-hidden">
                <img
                  src={card.image}
                  alt={`${card.title} 分类`}
                  title={`${card.title} 风驰影院分类入口`}
                  className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-foreground-950/45" />
                <div className="absolute inset-0 flex items-center gap-3 px-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500 text-secondary-950">
                    <i className={`${card.icon} text-2xl`} />
                  </span>
                  <span className="font-heading text-xl font-extrabold text-background-50">{card.title}</span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 p-4">
                <p className="text-xs leading-relaxed text-foreground-600 md:text-sm">{card.desc}</p>
                <i className="ri-arrow-right-line shrink-0 text-xl text-primary-500 transition group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </section>

        {/* 最新电影推荐 */}
        <section className="mt-14">
          <SectionHeading
            icon="ri-movie-2-line"
            title="最新电影推荐"
            subtitle="近期热度与口碑兼具的电影作品"
            moreTo="/category/movie"
          />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-6">
            {latestMovies.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        {/* 最新电视剧推荐 */}
        <section className="mt-14">
          <SectionHeading
            icon="ri-tv-2-line"
            title="最新电视剧推荐"
            subtitle="值得一追的热门剧集"
            moreTo="/category/tv"
          />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-6">
            {latestTvs.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        {/* 最新影视资讯 */}
        <section className="mt-14">
          <SectionHeading
            icon="ri-newspaper-line"
            title="最新影视资讯"
            subtitle="掌握影视圈的最新动态与行业观察"
            moreTo="/category/news"
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_1fr]">
            <Link
              to={`/detail/news/${latestNews[0].id}`}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:border-primary-300"
            >
              <div className="relative h-56 w-full overflow-hidden md:h-72">
                <img
                  src={latestNews[0].cover}
                  alt={latestNews[0].title}
                  title={`${latestNews[0].title} 影视资讯`}
                  className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-secondary-950">
                  {latestNews[0].category}
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition group-hover:text-primary-600 md:text-xl">
                  {latestNews[0].title}
                </h3>
                <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-500">{latestNews[0].summary}</p>
              </div>
            </Link>
            <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
              {latestNews.slice(1).map((item) => (
                <NewsRow key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* 品牌简介 */}
        <section className="mt-14 overflow-hidden rounded-lg bg-primary-500">
          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-3 md:p-10">
            <div className="md:col-span-2">
              <h2 className="font-heading text-xl font-extrabold text-background-50 md:text-3xl">关于风驰影院</h2>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-primary-100 md:text-base">
                风驰影院致力于打造专业的影视内容展示与资讯平台，围绕"最新电影电视剧播放与影视资讯"主题，为用户提供丰富的影视资源分类浏览、作品详情查询与权威行业资讯。我们持续更新热门影视动态，帮助你轻松筛选并了解自己感兴趣的作品。
              </p>
              <p className="mt-3 text-xs text-primary-200">
                温馨提示：本站仅提供影视资源展示与信息查询，暂不提供在线播放与下载功能。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3 md:grid-cols-1">
              {[
                { value: '20+', label: '影视作品' },
                { value: '8', label: '资讯专题' },
                { value: '3', label: '内容分类' }
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-primary-600/60 px-4 py-3 text-center md:text-left">
                  <p className="font-heading text-2xl font-extrabold text-accent-400">{stat.value}</p>
                  <p className="mt-1 text-xs text-primary-100">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}